import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Avatar, Box, Stack, Tooltip, Typography, useTheme} from '@mui/material';
import {Theme, alpha} from '@mui/material/styles';
import {amber, blueGrey, green, red} from '@mui/material/colors';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPhoneVolume, faTriangleExclamation, faVideo} from '@fortawesome/pro-solid-svg-icons';
import dayjs, {Dayjs} from 'dayjs';

import {CalendarEventDetail} from './calendarSources';
import {
    DAY_END_HOUR,
    DAY_START_HOUR,
    LABEL_MIN_PX,
    PlannerBar,
    PlannerRow,
    PlannerStatus,
    PresenceState,
} from './plannerModel';

/** Breite der Namensspalte. Bleibt beim Querscrollen stehen. */
export const NAME_COLUMN = 208;

/** Höhe einer Etage im Zeitband. */
const BAR_HEIGHT = 26;

/** Höhe des Ganztagsbands — flacher, es trägt weniger Information. */
const ALL_DAY_HEIGHT = 20;

/** Senkrechter Innenabstand einer Zeile. */
const ROW_PADDING = 8;

/** Stunden, die ein Tag beiträgt. */
const HOURS_PER_DAY = DAY_END_HOUR - DAY_START_HOUR;

/**
 * Erlaubte Abstände der Stundenlinien.
 *
 * Nur Teiler, die im Kopf aufgehen: alle zwei, drei, vier oder sechs Stunden liest man als Raster,
 * „alle fünf" nicht. Der kleinste passende gewinnt.
 */
const HOUR_STEPS = [1, 2, 3, 4, 6];

/**
 * Mindestabstand zweier Stundenlinien in Pixeln.
 *
 * Enger gesetzt wird aus Linien Fläche: in der Wochenansicht standen sie 30 Pixel auseinander, und
 * die Tafel las sich als graues Streifenmuster statt als Zeitachse.
 */
const MIN_HOUR_LINE_GAP = 72;

interface Props {
    rows: PlannerRow[];
    days: Dayjs[];
    /** Breite einer Stunde in Pixeln — bestimmt die gesamte Geometrie. */
    pixelsPerHour: number;
    /** Position der Jetzt-Linie in Pixeln, oder `null` wenn heute nicht im Blick ist. */
    nowPosition: number | null;
    /** Die aktuelle Uhrzeit als Beschriftung des Strahls. */
    nowLabel: string;
    /** Kennungen der angemeldeten Person — ihre Zeile wird hervorgehoben. */
    selfIdentifiers: string[];
    /**
     * Klick auf einen Balken. `ownMailbox` sagt, ob die Zeile die eigene ist — der Detail-Dialog
     * blendet danach den Knopf „In Outlook öffnen" ein oder aus, denn der Link führt immer in das
     * Postfach des Besitzers.
     */
    onSelect: (detail: CalendarEventDetail, ownMailbox: boolean) => void;
    /** Eine Raste des stufenlosen Zooms — ausgelöst mit Strg/⌘ und dem Mausrad. */
    onZoom?: (direction: 1 | -1) => void;
}

/**
 * Die Planungstafel: eine Zeile je Person, waagerechte Zeitachse.
 *
 * **Alles rechnet in Pixeln, nicht in Prozent.** Eine Stunde ist überall gleich breit — nur so
 * fluchten Balken und Raster, und nur so passt in einen Termin überhaupt Text. Vorher dehnte sich
 * das Raster auf die Fensterbreite, während die Balken fest gerechnet waren; deshalb standen die
 * Balken neben ihren Spalten. Wird der Zeitraum breiter als das Fenster, scrollt die Tafel
 * waagerecht und die Namensspalte bleibt stehen.
 *
 * **Zur Gestaltung der Balken:** getönte Fläche mit farbiger Kante links und dunkler Schrift —
 * dieselbe Sprache wie im Kalender. Vollflächige Farbe mit weißer Schrift ließ die Tafel wie einen
 * Bausteinkasten aussehen: bei sechs Kategorien wird jede Fläche zum Signal, und am Ende schreit
 * alles gleich laut.
 */
export const PlannerBoard: React.FC<Props> = ({
    rows, days, pixelsPerHour, nowPosition, nowLabel, selfIdentifiers, onSelect, onZoom,
}) => {
    const theme = useTheme();
    const {t, i18n} = useTranslation();

    const scrollRef = useRef<HTMLDivElement | null>(null);

    /**
     * Wohin nach dem Zoomen gescrollt werden muss, damit die Zeit **unter dem Zeiger** stehen
     * bleibt. Gemerkt wird sie als Stundenwert, nicht als Pixel — Pixel überleben den Zoom nicht.
     */
    const zoomAnchor = useRef<{hours: number; offset: number} | null>(null);

    /*
     * Bewusst ein eigener Zuhörer statt `onWheel`: React hängt Radereignisse passiv ein, und ein
     * passiver Zuhörer darf das Blättern nicht unterdrücken. Ohne `preventDefault` würde die Seite
     * beim Zoomen zusätzlich scrollen — und unter Windows zoomt der Browser dann die ganze Seite.
     */
    useEffect(() => {
        const element = scrollRef.current;
        if (!element || !onZoom) return undefined;

        const handleWheel = (event: WheelEvent): void => {
            // Strg (bzw. ⌘) ist die Geste, die überall zoomt; Zwei-Finger-Kneifen meldet der
            // Browser genauso. Ohne die Taste bleibt es beim normalen Blättern.
            if (!event.ctrlKey && !event.metaKey) return;
            event.preventDefault();

            const offset = event.clientX - element.getBoundingClientRect().left;
            const contentX = element.scrollLeft + offset - NAME_COLUMN;
            zoomAnchor.current = {hours: contentX / pixelsPerHour, offset};

            onZoom(event.deltaY < 0 ? 1 : -1);
        };

        element.addEventListener('wheel', handleWheel, {passive: false});
        return () => element.removeEventListener('wheel', handleWheel);
    }, [onZoom, pixelsPerHour]);

    /*
     * Nach dem Neuzeichnen die Bildlaufposition nachführen. `useLayoutEffect`, damit das vor dem
     * Anzeigen passiert — sonst sieht man den Sprung.
     */
    useLayoutEffect(() => {
        const element = scrollRef.current;
        const anchor = zoomAnchor.current;
        if (!element || !anchor) return;

        zoomAnchor.current = null;
        element.scrollLeft = anchor.hours * pixelsPerHour + NAME_COLUMN - anchor.offset;
    }, [pixelsPerHour]);

    const dayWidth = HOURS_PER_DAY * pixelsPerHour;
    const trackWidth = days.length * dayWidth;
    const hours = Array.from({length: HOURS_PER_DAY}, (_, i) => DAY_START_HOUR + i);

    /**
     * Abstand der Stundenlinien.
     *
     * Eine Linie je Stunde ergab in der Wochenansicht rund sechzig senkrechte Striche — dieselbe
     * Stärke wie die Tagesgrenzen, und damit war genau die Gliederung nicht mehr zu sehen, auf die
     * es ankommt. Der Abstand wächst deshalb mit der Zoomstufe: gezeichnet wird nur, was noch
     * Orientierung gibt statt Fläche zu füllen.
     */
    const hourStep = HOUR_STEPS.find((step) => step * pixelsPerHour >= MIN_HOUR_LINE_GAP)
        ?? HOURS_PER_DAY;

    /** Die Stundenlinien innerhalb eines Tages — die Tagesgrenze zeichnet der Tag selbst. */
    const hourLines: number[] = [];
    for (let offset = hourStep; offset < HOURS_PER_DAY; offset += hourStep) hourLines.push(offset);

    /** Beschriftet wird, was auch eine Linie bekommt — sonst zeigen Zahl und Strich verschiedenes. */
    const showHours = pixelsPerHour * hourStep >= 30 && dayWidth >= 150;
    /** Bei feiner Stufe reicht der abgekürzte Wochentag. */
    const compactDays = dayWidth < 130;

    /** Hintergrundraster einer Zeile — dieselben festen Breiten wie der Kopf. */
    const grid = (
        <Box sx={{position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none'}}>
            {days.map((day) => (
                <Box key={day.valueOf()} sx={{
                    width: dayWidth,
                    flexShrink: 0,
                    position: 'relative',
                    // Die Tagesgrenze ist die einzige Linie in voller Stärke: sie trägt die
                    // Gliederung, an der man sich zuerst orientiert.
                    borderLeft: 1,
                    borderColor: 'divider',
                    bgcolor: dayTint(day, theme),
                }}>
                    {hourLines.map((offset) => (
                        <Box key={offset} sx={{
                            position: 'absolute',
                            left: offset * pixelsPerHour,
                            top: 0,
                            bottom: 0,
                            borderLeft: 1,
                            borderColor: alpha(theme.palette.divider, 0.3),
                        }}/>
                    ))}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box ref={scrollRef} sx={{overflowX: 'auto'}}>
            <Box sx={{width: NAME_COLUMN + trackWidth, position: 'relative'}}>

                {/* Kopfzeile mit den Tagen. */}
                <Stack direction="row" sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}>
                    <Box sx={{
                        width: NAME_COLUMN,
                        flexShrink: 0,
                        borderRight: 1,
                        borderColor: 'divider',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                    }}/>

                    <Box sx={{width: trackWidth, flexShrink: 0, position: 'relative', display: 'flex'}}>
                        {nowPosition !== null && (
                            <Box sx={{
                                position: 'absolute',
                                left: nowPosition,
                                bottom: 2,
                                transform: 'translateX(-50%)',
                                zIndex: 6,
                                px: 0.75,
                                py: '1px',
                                borderRadius: '999px',
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                lineHeight: 1.6,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                {nowLabel}
                            </Box>
                        )}

                        {days.map((day) => {
                            const today = day.isSame(dayjs(), 'day');
                            return (
                                <Box key={day.valueOf()} sx={{
                                    width: dayWidth,
                                    flexShrink: 0,
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                    bgcolor: dayTint(day, theme),
                                    pt: 0.75,
                                    pb: showHours ? 0 : 0.75,
                                }}>
                                    <Typography variant="caption" sx={{
                                        display: 'block',
                                        textAlign: 'center',
                                        fontWeight: today ? 700 : 600,
                                        letterSpacing: '0.04em',
                                        color: today ? 'primary.main' : 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                    }}>
                                        {compactDays
                                            ? `${weekday(day, i18n.language)} ${day.format('DD.')}`
                                            : `${weekday(day, i18n.language)} ${day.format('DD.MM.')}`}
                                    </Typography>

                                    {showHours && (
                                        <Box sx={{display: 'flex', mt: 0.25}}>
                                            {hours.map((hour, index) => (
                                                <Box key={hour} sx={{width: pixelsPerHour, flexShrink: 0}}>
                                                    {index % hourStep === 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{
                                                            display: 'block',
                                                            textAlign: 'center',
                                                            fontSize: '0.65rem',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            // Über leere Nachbarzellen hinausragen dürfen:
                                                            // bei großem Abstand ist die eigene Zelle schmal.
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {String(hour).padStart(2, '0')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Stack>

                <Box sx={{position: 'relative'}}>
                    {/*
                      * Der Jetzt-Strahl liegt als eine Bahn über allen Zeilen statt als Strich in
                      * jeder — nur so liest man ihn über die ganze Tafel hinweg als eine Linie.
                      * Alles links davon wird leicht abgedunkelt: „vorbei" muss man nicht mehr
                      * planen, und der Blick landet von selbst auf dem, was noch kommt.
                      */}
                    {nowPosition !== null && (
                        <Box sx={{
                            position: 'absolute',
                            left: NAME_COLUMN,
                            width: trackWidth,
                            top: 0,
                            bottom: 0,
                            zIndex: 3,
                            pointerEvents: 'none',
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                left: 0,
                                width: nowPosition,
                                top: 0,
                                bottom: 0,
                                bgcolor: alpha(theme.palette.common.black, 0.035),
                            }}/>
                            <Box sx={{
                                position: 'absolute',
                                left: nowPosition,
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                bgcolor: 'error.main',
                            }}/>
                        </Box>
                    )}

                    {rows.map((row, index) => {
                        const bandHeight = row.stacks * BAR_HEIGHT + (row.stacks - 1) * 4;
                        const isSelf = selfIdentifiers.some(
                            (id) => !!id && id.toLowerCase() === row.identifier.toLowerCase());

                        return (
                            <Stack key={row.identifier} direction="row" sx={{
                                borderTop: index === 0 ? 0 : 1,
                                borderColor: 'divider',
                                bgcolor: isSelf
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : index % 2 === 1 ? alpha(theme.palette.action.hover, 0.3) : undefined,
                                minHeight: Math.max(
                                    ROW_PADDING * 2 + bandHeight + (row.hasAllDay ? ALL_DAY_HEIGHT + 6 : 0),
                                    52),
                            }}>
                                <Stack direction="row" spacing={1.25} sx={{
                                    width: NAME_COLUMN,
                                    flexShrink: 0,
                                    alignItems: 'center',
                                    px: 1.5,
                                    borderRight: 1,
                                    borderColor: 'divider',
                                    // Bleibt beim Querscrollen stehen — Balken ohne Namen sind wertlos.
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 4,
                                    bgcolor: isSelf
                                        ? alpha(theme.palette.primary.main, 0.1)
                                        : 'background.paper',
                                    // Die eigene Zeile trägt zusätzlich eine Marke am linken Rand.
                                    borderLeft: isSelf ? 3 : 0,
                                    borderLeftColor: 'primary.main',
                                }}>
                                    {/*
                                      * Der Teams-Punkt sitzt am Avatar, weil er dort gelernt ist —
                                      * dieselbe Stelle, dieselben Farben wie in Teams selbst.
                                      */}
                                    <Tooltip title={row.presence ? t(row.presence.key) : ''}>
                                        <Box sx={{position: 'relative', flexShrink: 0, display: 'flex'}}>
                                            <Avatar sx={{
                                                width: 30, height: 30, fontSize: '0.75rem', fontWeight: 600,
                                                bgcolor: isSelf
                                                    ? 'primary.main'
                                                    : alpha(theme.palette.primary.main, 0.12),
                                                color: isSelf ? 'primary.contrastText' : 'primary.main',
                                            }}>
                                                {initials(row.label)}
                                            </Avatar>
                                            {row.presence && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    right: -1,
                                                    bottom: -1,
                                                    width: 11,
                                                    height: 11,
                                                    borderRadius: '50%',
                                                    bgcolor: PRESENCE_COLOR[row.presence.state],
                                                    // Der Ring trennt den Punkt vom Avatar darunter;
                                                    // ohne ihn verschwimmt er auf dunklen Initialen.
                                                    border: 2,
                                                    borderColor: isSelf
                                                        ? alpha(theme.palette.primary.main, 0.1)
                                                        : 'background.paper',
                                                    boxSizing: 'content-box',
                                                }}/>
                                            )}
                                        </Box>
                                    </Tooltip>
                                    <Box sx={{minWidth: 0}}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: isSelf ? 700 : 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {row.label}
                                        </Typography>
                                        {/*
                                          * „Wer kann jetzt?" ist die Frage, für die man eine
                                          * Planungstafel aufmacht — die Antwort steht am Namen.
                                          */}
                                        {row.presence ? (
                                            /*
                                             * Liegt ein Teams-Status vor, hat er den Vortritt: er sagt,
                                             * was **gerade** ist, während der Kalender nur sagt, was
                                             * geplant war. Der Kalenderzustand bleibt im Tooltip — er
                                             * beantwortet die andere, ebenso häufige Frage: „wie lange
                                             * noch?"
                                             */
                                            <Tooltip title={presenceTooltip(row, t)}>
                                                <Stack direction="row" spacing={0.5}
                                                       sx={{alignItems: 'center', minWidth: 0}}>
                                                    {row.presence.inCall && (
                                                        <Box component="span" sx={{
                                                            color: PRESENCE_COLOR[row.presence.state],
                                                            display: 'inline-flex',
                                                            fontSize: '0.65rem',
                                                            flexShrink: 0,
                                                        }}>
                                                            <FontAwesomeIcon icon={faPhoneVolume}/>
                                                        </Box>
                                                    )}
                                                    <Typography variant="caption" sx={{
                                                        color: PRESENCE_COLOR[row.presence.state],
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {t(row.presence.key)}
                                                    </Typography>
                                                </Stack>
                                            </Tooltip>
                                        ) : row.status !== 'unknown' && (
                                            <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                                <Box sx={{
                                                    width: 7, height: 7, borderRadius: '50%',
                                                    bgcolor: STATUS_COLOR[row.status],
                                                    flexShrink: 0,
                                                }}/>
                                                <Typography variant="caption" color="text.secondary" sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {statusText(row.status, row.statusUntil, t)}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                    {row.notes.length > 0 && (
                                        <Tooltip title={row.notes.join(' · ')}>
                                            <Box component="span" sx={{
                                                color: 'warning.main', display: 'inline-flex', ml: 'auto',
                                            }}>
                                                <FontAwesomeIcon icon={faTriangleExclamation}/>
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Stack>

                                <Box sx={{
                                    width: trackWidth,
                                    flexShrink: 0,
                                    position: 'relative',
                                    py: `${ROW_PADDING}px`,
                                }}>
                                    {grid}

                                    {row.hasAllDay && (
                                        <Box sx={{position: 'relative', height: ALL_DAY_HEIGHT, mb: '6px'}}>
                                            {row.bars.filter((bar) => bar.lane === 'allDay').map((bar) => (
                                                <Bar key={bar.key} bar={bar}
                                                     onSelect={(d) => onSelect(d, isSelf)}/>
                                            ))}
                                        </Box>
                                    )}

                                    <Box sx={{position: 'relative', height: bandHeight}}>
                                        {row.bars.filter((bar) => bar.lane === 'timed').map((bar) => (
                                            <Bar key={bar.key} bar={bar}
                                                 onSelect={(d) => onSelect(d, isSelf)}
                                                 top={bar.stack * (BAR_HEIGHT + 4)} height={BAR_HEIGHT}/>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * Ein Balken.
 *
 * Getönte Fläche, farbige Kante links, dunkle Schrift. Online-Besprechungen tragen zusätzlich ein
 * Kamerasymbol — man sieht damit auf der Tafel sofort, welcher Termin am Schreibtisch stattfindet
 * und welcher eine Anfahrt bedeutet.
 */
const Bar: React.FC<{
    bar: PlannerBar;
    onSelect: (detail: CalendarEventDetail) => void;
    top?: number;
    height?: number;
}> = ({bar, onSelect, top, height}) => {
    const showLabel = bar.width >= LABEL_MIN_PX;
    const showIcon = bar.online && bar.width >= 18;

    const tint = alpha(bar.color, bar.free ? 0.08 : 0.18);
    const hoverTint = alpha(bar.color, 0.3);

    return (
        <Tooltip title={bar.tooltip} placement="top" arrow>
            <Box onClick={() => onSelect(bar.detail)} sx={{
                position: 'absolute',
                left: bar.left,
                width: bar.width,
                top: top ?? 0,
                height: height ?? '100%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                borderRadius: '4px',
                /*
                 * **Deckende Fläche.** Die Tönung liegt als Verlauf über einer undurchsichtigen
                 * Grundfarbe, statt selbst der Hintergrund zu sein: eine halbdurchsichtige Füllung
                 * ließ das Raster durch die Balken hindurchscheinen, und die Striche zerschnitten
                 * jeden Termin in Stücke. Der Farbeindruck bleibt derselbe, nur liegt der Balken
                 * jetzt wirklich auf dem Raster statt darin.
                 */
                bgcolor: 'background.paper',
                backgroundImage: `linear-gradient(${tint}, ${tint})`,
                borderLeft: '3px solid',
                borderColor: bar.color,
                outline: bar.free ? `1px dashed ${alpha(bar.color, 0.7)}` : 'none',
                outlineOffset: '-1px',
                overflow: 'hidden',
                zIndex: 2,
                transition: 'background-image 120ms',
                '&:hover': {backgroundImage: `linear-gradient(${hoverTint}, ${hoverTint})`},
            }}>
                {showIcon && (
                    <Box component="span" sx={{
                        color: bar.color, display: 'inline-flex', fontSize: '0.62rem', flexShrink: 0,
                    }}>
                        <FontAwesomeIcon icon={faVideo}/>
                    </Box>
                )}
                {showLabel && (
                    <Typography variant="caption" sx={{
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {bar.title}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

/** Ampelfarben des Zustands — grün heißt „jetzt ansprechbar". */
const STATUS_COLOR: Record<Exclude<PlannerStatus, 'unknown'>, string> = {
    free: green[600],
    busy: amber[800],
    absent: blueGrey[400],
};

/**
 * Farben des Teams-Status — dieselben, die Teams selbst verwendet.
 *
 * Bewusst nicht die Ampelfarben der Tafel: die stehen für den geplanten Zustand, diese für den
 * gemeldeten. Gleiche Farben für beides würde die zwei Aussagen zu einer verschmelzen.
 */
const PRESENCE_COLOR: Record<PresenceState, string> = {
    available: green[600],
    busy: red[600],
    doNotDisturb: red[900],
    away: amber[700],
    offline: blueGrey[400],
};

/** Teams-Status und Kalenderzustand nebeneinander — die zwei Antworten auf „geht das jetzt?". */
const presenceTooltip = (
    row: PlannerRow,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => [
    row.presence ? t(row.presence.key) : '',
    row.status !== 'unknown' ? t('planner.presenceCalendar', {
        status: statusText(row.status, row.statusUntil, t),
    }) : '',
].filter(Boolean).join(' · ');

/** Zustand als Text, mit dem Zeitpunkt, bis wann er gilt. */
const statusText = (
    status: PlannerStatus,
    until: string | undefined,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => {
    if (status === 'absent') return t('planner.statusAbsent');
    if (status === 'busy') {
        return until ? t('planner.statusBusyUntil', {time: until}) : t('planner.statusBusy');
    }
    return until ? t('planner.statusFreeUntil', {time: until}) : t('planner.statusFree');
};

/** Wochentag in der Sprache der Oberfläche. */
const weekday = (day: Dayjs, locale: string): string =>
    day.toDate().toLocaleDateString(locale, {weekday: 'short'});

/** Hintergrund einer Tagesspalte: heute betont, Wochenende gedämpft. */
const dayTint = (day: Dayjs, theme: Theme): string | undefined => {
    if (day.isSame(dayjs(), 'day')) return alpha(theme.palette.primary.main, 0.05);
    if (day.day() === 0 || day.day() === 6) return alpha(theme.palette.action.disabledBackground, 0.4);
    return undefined;
};

/** Initialen aus „Nachname, Vorname" oder „Vorname Nachname". */
const initials = (label: string): string => {
    const parts = label.replace(',', ' ').split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

export default PlannerBoard;
